/**
 * Incremental tree builder for hierarchical data
 * Builds the tree in-place as chunks arrive, avoiding large copy operations
 * Memory efficient - no intermediate flat array needed
 */

/**
 * Extract value from record using simple path or template
 */
function extractValue (record, path) {
  if (typeof path === 'string') {
    const fieldName = path.replace(/^\$\./, '')
    return record[fieldName]
  } else if (Array.isArray(path) && path.length === 1) {
    const fieldName = path[0].replace(/^\$\./, '')
    return record[fieldName] !== undefined ? [record[fieldName]] : []
  } else if (typeof path === 'object' && path !== null) {
    const result = {}
    for (const [key, value] of Object.entries(path)) {
      const extracted = extractValue(record, value)
      if (extracted !== undefined) {
        result[key] = extracted
      }
    }
    return Object.keys(result).length > 0 ? result : undefined
  }
  return path
}

/**
 * Incremental tree builder class
 * Maintains state as chunks are processed, builds tree in-place
 */
export class IncrementalTreeBuilder {
  constructor (hierarchySpec) {
    this.spec = hierarchySpec
    // Use Map for efficient grouping - preserves insertion order
    this.groups = new Map()
  }

  /**
   * Process a chunk of rows, inserting into the tree structure
   * Can be called multiple times as chunks arrive
   */
  addChunk (rows) {
    if (!rows || rows.length === 0) return

    for (const row of rows) {
      this._addRowToLevel(row, this.groups, this.spec)
    }
  }

  /**
   * Recursively add a row to the appropriate level of the hierarchy
   */
  _addRowToLevel (row, parentGroups, levelSpec) {
    // Build composite key from groupBy fields
    const keyParts = []
    let hasAllKeys = true

    for (const field of levelSpec.groupBy) {
      const value = row[field]
      if (value === undefined || value === null) {
        hasAllKeys = false
        break
      }
      keyParts.push(JSON.stringify(value))
    }

    if (!hasAllKeys) return

    const key = keyParts.join('|')

    // Get or create group
    if (!parentGroups.has(key)) {
      parentGroups.set(key, {
        representative: row,
        items: [],
        children: levelSpec.child ? new Map() : null
      })
    }

    const group = parentGroups.get(key)
    group.items.push(row)

    // Recursively process children
    if (levelSpec.child) {
      this._addRowToLevel(row, group.children, levelSpec.child)
    }
  }

  /**
   * Finalize the tree - convert Map structure to final array format
   * This is much faster than rebuilding from scratch since structure already exists
   */
  finalize () {
    return {
      data: this._serializeLevel(this.groups, this.spec)
    }
  }

  /**
   * Convert Map-based groups to final array structure
   */
  _serializeLevel (groups, levelSpec) {
    const results = []

    for (const [, group] of groups) {
      const node = {}

      // Extract fields for this level
      if (levelSpec.take) {
        for (const [targetKey, sourcePath] of Object.entries(levelSpec.take)) {
          // Handle array aggregation across the group
          if (Array.isArray(sourcePath) && sourcePath.length === 1) {
            const fieldName = sourcePath[0].replace(/^\$\./, '')
            const values = group.items
              .map(item => item[fieldName])
              .filter(v => v !== undefined)
            if (values.length > 0) {
              node[targetKey] = values
            }
          } else {
            const value = extractValue(group.representative, sourcePath)
            if (value !== undefined) {
              node[targetKey] = value
            }
          }
        }
      }

      // Recursively serialize children
      if (group.children && group.children.size > 0) {
        node.data = this._serializeLevel(group.children, levelSpec.child)
      }

      results.push(node)
    }

    return results
  }

  /**
   * Get current memory stats (for monitoring)
   */
  getStats () {
    return {
      topLevelGroups: this.groups.size,
      totalGroups: this._countGroups(this.groups)
    }
  }

  _countGroups (groups) {
    let count = groups.size
    for (const [, group] of groups) {
      if (group.children) {
        count += this._countGroups(group.children)
      }
    }
    return count
  }
}

/**
 * Build tree incrementally from chunks
 * More memory efficient than buildTree - no intermediate flat array
 *
 * @param {Object} spec - Hierarchy specification
 * @param {Array<Array>} chunks - Array of row chunks
 * @returns {Object} - Hierarchical tree
 */
export function buildTreeFromChunks (spec, chunks) {
  const builder = new IncrementalTreeBuilder(spec)

  for (const chunk of chunks) {
    builder.addChunk(chunk)
  }

  return builder.finalize()
}

/**
 * Streaming version - build tree as chunks arrive
 * Returns builder instance for incremental updates
 *
 * @param {Object} spec - Hierarchy specification
 * @returns {IncrementalTreeBuilder} - Builder instance
 */
export function createIncrementalBuilder (spec) {
  return new IncrementalTreeBuilder(spec)
}
